import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Popconfirm, Spin, Typography, message } from 'antd';
import { SendOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getTelegramAccount, disconnectTelegram } from '../../services/api';

const { Text, Paragraph } = Typography;

export default function BotTelegramAccountSection({
  keycloak,
  botId,
  hasCredentials,
  onDisconnected,
}) {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);

  const loadAccount = useCallback(async () => {
    if (!hasCredentials || !keycloak?.token) {
      setAccount(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getTelegramAccount(keycloak, botId);
      setAccount(data);
    } catch (e) {
      setAccount(null);
      const msg = e.message || 'Could not load linked account';
      if (!msg.includes('404') && !msg.toLowerCase().includes('no telegram')) {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [hasCredentials, keycloak, botId]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectTelegram(keycloak, botId);
      message.success('Telegram account disconnected');
      setAccount(null);
      onDisconnected?.();
    } catch (e) {
      message.error(e.message || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  if (!hasCredentials) {
    return (
      <Alert
        type="info"
        showIcon
        message="No Telegram account linked"
        description="Connect a Telegram account to enable messaging for this bot."
        action={(
          <Button size="small" type="primary" onClick={() => navigate(`/channels/bots/${botId}/connect`)}>
            Connect
          </Button>
        )}
      />
    );
  }

  return (
    <div className="bot-telegram-account">
      <Spin spinning={loading}>
        {error && (
          <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />
        )}
        {account && (
          <div className="bot-telegram-account-card">
            <div className="bot-telegram-account-icon">
              <SendOutlined />
            </div>
            <div className="bot-telegram-account-details">
              <Text strong>{account.display_name}</Text>
              {account.username && (
                <div><Text type="secondary">@{account.username}</Text></div>
              )}
              {account.phone && (
                <div><Text type="secondary">{account.phone}</Text></div>
              )}
              <div><Text type="secondary">ID {account.id}</Text></div>
            </div>
          </div>
        )}
        {!loading && !account && !error && (
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            <UserOutlined /> Telegram account linked
          </Paragraph>
        )}
        <Popconfirm
          title="Disconnect Telegram account?"
          description="The bot will stop receiving messages until you sign in again."
          onConfirm={handleDisconnect}
          okText="Disconnect"
          okType="danger"
          cancelText="Cancel"
        >
          <Button danger loading={disconnecting} disabled={loading}>
            Disconnect account
          </Button>
        </Popconfirm>
      </Spin>
    </div>
  );
}
